import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { initializeApp } from 'firebase/app';
import { getAuth, SAMLAuthProvider, signInWithPopup, signOut } from 'firebase/auth';
import { env } from '../../../env';
import { AsyncThunkConfig } from '../../async-thunk-config';
import {
  AdminConsoleLoginError,
  AdminConsoleState,
  AdminConsoleUser,
} from './admin-console-types';

const allowedGroup = env.adminConsoleMemberOfGroup;

const initialState: AdminConsoleState = {
  user: null,
  loginStatus: 'idle',
};

// memberOf arriva come stringa per un solo gruppo e come lista per più gruppi
const belongsToAllowedGroup = (memberOf: unknown): boolean => {
  const groups = Array.isArray(memberOf) ? memberOf : [memberOf];
  return (
    !!allowedGroup &&
    groups.some(
      (group) =>
        typeof group === 'string' &&
        group.toLowerCase() === allowedGroup.toLowerCase()
    )
  );
};

const provider = new SAMLAuthProvider(env.firebaseProvider);
export const adminConsoleLogin = createAsyncThunk<
  void,
  void,
  AsyncThunkConfig<AdminConsoleLoginError>
>('admin-console-login', async (_, thunkApi) => {
  try {
    const config = {
      apiKey: env.firebaseApiKey,
      authDomain: env.firebaseAuthDomain,
    };

    const auth = getAuth(initializeApp(config));

    const userCred = await signInWithPopup(auth, provider);
    const credential = await userCred?.user?.getIdTokenResult();
    const attributes = (credential?.claims?.firebase as any)
      ?.sign_in_attributes;
    if (!userCred?.user) {
      return thunkApi.rejectWithValue('GENERIC_ERROR');
    }
    if (!belongsToAllowedGroup(attributes?.memberOf)) {
      // l'utente SAML non è abilitato: chiudo la sessione Firebase appena aperta
      await signOut(auth).catch(() => undefined);
      return thunkApi.rejectWithValue('NOT_AUTHORIZED');
    }
    thunkApi.dispatch(
      adminConsoleSliceActions.setUser({
        token: credential.token,
        name: attributes?.NOME,
        surname: attributes?.COGNOME,
      })
    );
    return null;
  } catch (error) {
    console.error('Login error:', error);
    return thunkApi.rejectWithValue('GENERIC_ERROR');
  }
});

const adminConsoleSlice = createSlice({
  name: 'features/adminConsole/common',
  initialState,
  reducers: {
    setUser(state, action: PayloadAction<AdminConsoleUser>) {
      state.user = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(adminConsoleLogin.pending, (state) => {
        state.loginStatus = 'loading';
        state.loginError = undefined;
      })
      .addCase(adminConsoleLogin.fulfilled, (state) => {
        state.loginStatus = 'succeeded';
      })
      .addCase(adminConsoleLogin.rejected, (state, action) => {
        state.loginStatus = 'failed';
        state.loginError = action.payload ?? 'GENERIC_ERROR';
      });
  },
});

export const adminConsoleSliceActions = adminConsoleSlice.actions;

export default adminConsoleSlice.reducer;
